import { createEffect, createSignal } from "solid-js";
import type { DirectionsMode, Position } from "../../shared/api";
import { directions, needsNewRoute } from "../directions";

/**
 * A path on the map from `from` to `to` that follows the streets (Pony's direction API), kept up to
 * date as the rider moves without asking a route per GPS fix. Until it arrives, or if routing fails,
 * the straight line, so the map never shows nothing.
 */
export function createRoutePath(
  from: () => Position | undefined,
  to: () => Position | undefined,
  mode: DirectionsMode,
) {
  const [routed, setRouted] = createSignal<{ to: Position; path: Position[] }>();
  let last: { from: Position; to: Position } | undefined;
  createEffect(() => {
    const a = from();
    const b = to();
    if (!a || !b) {
      last = undefined;
      return;
    }
    if (!needsNewRoute(last, a, b)) return;
    const asked = { from: a, to: b };
    last = asked;
    directions
      .get(a, b, mode)
      .then((r) => {
        if (last === asked) setRouted({ to: b, path: [a, ...r.path, b] });
      })
      .catch(() => {
        if (last === asked) last = undefined; // ask again on the next move
      });
  });
  return (): Position[] | undefined => {
    const a = from();
    const b = to();
    if (!a || !b) return undefined;
    const r = routed();
    return r && r.to.lat === b.lat && r.to.lng === b.lng ? r.path : [a, b];
  };
}
